/**
 * Central translations file — English (en) and Bahasa Malaysia (bm).
 *
 * How to add BM text:
 *   1. Find the page section below (e.g. `bm.ageSmoker`).
 *   2. Replace the English string with the Bahasa Malaysia equivalent.
 *   3. The `useTranslation()` hook will automatically pick the right copy.
 *
 * Any BM string left identical to EN is just a placeholder — update it when you have the copy.
 */

export type Lang = 'en' | 'bm';

// ─── Shape ───────────────────────────────────────────────────────────────────

export type AppTranslations = {
  home: {
    brownLine1: string;
    brownLine2: string;
    brownLine3: string;
    brownLine4: string;
    brownLine5: string;
    brownLine6: string;
  };
  ageSmoker: {
    title: string;
    smokerQuestion: string;
    yes: string;
    no: string;
    submit: string;
    allRequiredWarning: string;
  };
  pdpa: {
    title: string;
    consentText: string;
    agree: string;
    decline: string;
    consentWarning: string;
    noticeTitle: string;
  };
  uniqueCode: {
    instruction: string;
    mobileLabel: string;
    uniqueCodeLabel: string;
    findCodeHint: string;
    requestOtpButton: string;
    mobileWarning: string;
    mobileInvalidWarning: string;
    uniqueCodeWarning: string;
    codeUsedWarning: string;
    codeNotFoundWarning: string;
    verifyError: string;
    otpError: string;
    turnstileFailed: string;
    confirmTitle: string;
    confirmMobileLabel: string;
    confirmUniqueCodeLabel: string;
    confirmPrompt: string;
    verifyingSecurity: string;
    back: string;
    confirm: string;
  };
  otp: {
    sentMessage: string;
    otpCodeLabel: string;
    submit: string;
    emptyWarning: string;
    verifyError: string;
    /** Use {{seconds}} placeholder for countdown whole seconds. */
    waitBeforeSubmit: string;
    readyToSubmit: string;
  };
  smokerProfile: {
    raceLabel: string;
    stateLabel: string;
    brandLabel: string;
    selectRace: string;
    selectState: string;
    selectBrand: string;
    loadingBrands: string;
    selectBrandFirst: string;
    selectVariant: string;
    loadingVariants: string;
    next: string;
    brandsLoadingWarning: string;
    variantsLoadingWarning: string;
    incompleteWarning: string;
  };
  q7_1: {
    question: string;
    disclaimer: string;
  };
  q7_2: {
    question: string;
  };
  q7_3: {
    question: string;
  };
  q7_4: {
    question: string;
    placeholder: string;
    searching: string;
    noResults: string;
    searchError: string;
    cantFind: string;
    submit: string;
  };
  devReg: {
    question: string;
    optionYes: string;
    optionCareContact: string;
    optionNoRegister: string;
  };
  tradeIn: {
    question: string;
    optionYesHelp: string;
    optionNotInterested: string;
    optionAlreadyNew: string;
    tnc: string;
    sessionExpiredError: string;
    mobileError: string;
    incompleteError: string;
    saveError: string;
  };
  tradeInContact: {
    instruction: string;
    mobileLabel: string;
    nameLabel: string;
    emailLabel: string;
    submit: string;
    mobileWarning: string;
    nameWarning: string;
    emailWarning: string;
    sessionExpiredWarning: string;
    incompleteWarning: string;
    pdpaNote: string;
    saveError: string;
  };
  thankYou: {
    title: string;
    subtitle: string;
  };
  tng: {
    title: string;
    subtitle: string;
    smsNote: string;
  };
};

// ─── English ─────────────────────────────────────────────────────────────────

const en: AppTranslations = {
  home: {
    brownLine1: 'Respondents who complete the',
    brownLine2: 'survey will be provided a token',
    brownLine3: "of appreciation.* T&C apply.",
    brownLine4: 'Choose your language to',
    brownLine5: 'proceed if you agree to',
    brownLine6: 'participate in the survey.',
  },
  ageSmoker: {
    title: 'Age',
    smokerQuestion: 'Are you a smoker?',
    yes: 'Yes',
    no: 'No',
    submit: 'Submit',
    allRequiredWarning: 'Please answer all questions before continuing to the next page.',
  },
  pdpa: {
    title: 'PDPA',
    consentText: 'I hereby confirm my consent to the processing of my personal data as set out above.',
    agree: 'Agree',
    decline: 'Decline',
    consentWarning: 'Please tick the terms and condition to continue.',
    noticeTitle: 'NOTICE UNDER THE PERSONAL DATA PROTECTION ACT 2010 ("PDPA")',
  },
  uniqueCode: {
    instruction: 'Please input your mobile number and survey unique code for us to send you an OTP.',
    mobileLabel: 'Mobile No.:',
    uniqueCodeLabel: 'Unique Code:',
    findCodeHint: '*Find the Unique Code behind the QR sticker.',
    requestOtpButton: 'Request OTP',
    mobileWarning: 'Please enter your mobile number.',
    mobileInvalidWarning: 'Please enter a valid Malaysian mobile number.',
    uniqueCodeWarning: 'Please enter your unique code.',
    codeUsedWarning: 'This unique code has already been redeemed.',
    codeNotFoundWarning: 'Unique code not found',
    verifyError: 'Unable to verify unique code. Please try again.',
    otpError: 'Unable to send OTP. Please try again.',
    turnstileFailed:
      'Security check could not complete. Please check your internet connection and try again.',
    confirmTitle: 'You have entered:',
    confirmMobileLabel: 'Mobile No: ',
    confirmUniqueCodeLabel: 'Unique Code: ',
    confirmPrompt: 'Please confirm the information is correct.',
    verifyingSecurity: 'Security verification in progress. Please wait...',
    back: 'Back',
    confirm: 'Confirm',
  },
  otp: {
    sentMessage: 'OTP has been sent to your mobile no.:',
    otpCodeLabel: 'OTP Code:',
    submit: 'Submit',
    emptyWarning: 'Please enter the OTP code.',
    verifyError: 'Unable to verify OTP. Please try again.',
    waitBeforeSubmit:
      'Wait {{seconds}}s before tapping Submit. You can type the code while you wait — do not tap Submit repeatedly.',
    readyToSubmit: 'You can tap Submit now.',
  },
  smokerProfile: {
    raceLabel: 'Race',
    stateLabel: 'State of Residence',
    brandLabel: 'Current Smoking Brand',
    selectRace: 'Select race',
    selectState: 'Select state',
    selectBrand: 'Select a brand',
    loadingBrands: 'Loading…',
    selectBrandFirst: 'Select a brand first',
    selectVariant: 'Select a variant',
    loadingVariants: 'Loading…',
    next: 'Next',
    brandsLoadingWarning: 'Please wait for the brand list to finish loading.',
    variantsLoadingWarning: 'Please wait for the variant list to finish loading.',
    incompleteWarning: 'Please answer all questions before continuing.',
  },
  q7_1: {
    question: 'Where do you normally buy your EVO refills?',
    disclaimer: '* Disclaimer: Only available in Klang Valley, Johor and Penang. Terms and conditions apply',
  },
  q7_2: {
    question: 'How many EVO refills do you buy each time?',
  },
  q7_3: {
    question: "Have you experienced 'Out Of Stock' in your recent EVO purchases?",
  },
  q7_4: {
    question: 'To help us improve your purchase experience, please help share store location',
    placeholder: 'Enter the building or street name',
    searching: 'Searching…',
    noResults: 'No matching outlets. Try different keywords.',
    searchError: 'Could not search outlets. Please try again.',
    cantFind: "Sorry, I can't find my location",
    submit: 'Submit',
  },
  devReg: {
    question: 'Have you registered your device?',
    optionYes: 'Yes',
    optionCareContact: 'No, I would like the Customer Care Team to contact me for the device registration',
    optionNoRegister: "No, I don't want to register my device",
  },
  tradeIn: {
    question: 'Would you like to trade in your Old Device?',
    optionYesHelp: 'Yes, I would like the Consumer Care Team to help me with the Device trade-in process.',
    optionNotInterested: 'No, I am not interested to trade-in my Device right now.',
    optionAlreadyNew: 'No, I am already using the new Device',
    tnc: '* Terms and conditions apply',
    sessionExpiredError: 'Session expired. Please start again from the unique code step.',
    mobileError: 'Mobile number is missing. Please go back to the unique code step.',
    incompleteError: 'Survey data is incomplete. Please go back and complete the previous steps.',
    saveError: 'Unable to save. Please try again.',
  },
  tradeInContact: {
    instruction: 'Kindly complete your information in the fields below.',
    mobileLabel: 'Mobile No.:',
    nameLabel: 'Name:',
    emailLabel: 'Email Address:',
    submit: 'Submit',
    mobileWarning: 'Please enter your mobile number.',
    nameWarning: 'Please enter your name.',
    emailWarning: 'Please enter a valid email address.',
    sessionExpiredWarning: 'Session expired. Please start again from the unique code step.',
    incompleteWarning: 'Survey data is incomplete. Please go back and complete the previous steps.',
    pdpaNote: '* By submitting your personal information, you agree and consent for us to use, collect and process your personal data for trade-in programme subjecting to the terms of our PDPA notice.',
    saveError: 'Unable to save. Please try again.',
  },
  thankYou: {
    title: 'THANK YOU',
    subtitle: 'FOR YOUR PARTICIPATION',
  },
  tng: {
    title: 'THANK YOU',
    subtitle: 'FOR YOUR PARTICIPATION',
    smsNote: 'You will receive the e-Wallet redemption PIN via SMS',
  },
};

// ─── Bahasa Malaysia ─────────────────────────────────────────────────────────
// TODO: Replace each string below with the correct Bahasa Malaysia translation.
// Strings still matching EN are placeholders — update them as BM copy is provided.

const bm: AppTranslations = {
  home: {
    brownLine1: 'Respondents who complete the',
    brownLine2: 'survey will be provided a token',
    brownLine3: "of appreciation.* T&C apply.",
    brownLine4: 'Choose your language to',
    brownLine5: 'proceed if you agree to',
    brownLine6: 'participate in the survey.',
  },
  ageSmoker: {
    title: 'Umur',
    smokerQuestion: 'Adakah anda seorang perokok?', 
    yes: 'Ya', 
    no: 'Tidak', 
    submit: 'Hantar', 
    allRequiredWarning: 'Sila jawab semua soalan sebelum meneruskan ke halaman seterusnya.', 
  },
  pdpa: {
    title: 'PDPA', 
    consentText: 'Dengan ini saya bersetuju dan mengesahkan persetujuan saya untuk memprocess data peribadi saya seperti dinyatakan di atas.', 
    agree: 'Setuju', 
    decline: 'Tidak Setuju', 
    consentWarning: 'Sila tandakan kotak pilihan untuk mengesahkan persetujuan anda', 
    noticeTitle: 'NOTIS DI BAWAH AKTA PERLINDUNGAN DATA PERIBADI 2010 (“PDPA")', 
  },
  uniqueCode: {
    instruction: 'Sila masukkan no. telefon dan & kod unik untuk menerima OTP', 
    mobileLabel: 'Telefon No.:', 
    uniqueCodeLabel: 'Kod Unik:', 
    findCodeHint: '* Cari kod unik di sebalik pelekat QR', 
    requestOtpButton: 'Minta OTP', 
    mobileWarning: 'Sila masukkan nombor telefon.', 
    mobileInvalidWarning: 'Sila masukkan nombor telefon bimbit Malaysia yang sah.',
    uniqueCodeWarning: 'Sila masukkan kod unik.', 
    codeUsedWarning: 'Kod unik ini telah ditebus.', 
    codeNotFoundWarning: 'Kod unik tidak ditemui', 
    verifyError: 'Tidak dapat mengesahkan kod unik. Sila cuba lagi.', 
    otpError: 'Tidak dapat menghantar OTP. Sila cuba lagi.', 
    turnstileFailed:
      'Pemeriksaan keselamatan tidak dapat diselesaikan. Sila semak sambungan internet anda dan cuba lagi.',
    confirmTitle: 'Anda telah memasukkan:', 
    confirmMobileLabel: 'Telefon No:', 
    confirmUniqueCodeLabel: 'Kod Unik', 
    confirmPrompt: 'Sila sahkan maklumat tersebut adalah betul.', 
    verifyingSecurity: 'Pengesahan keselamatan sedang diproses. Sila tunggu...',
    back: 'Kembali', 
    confirm: 'Sahkan', 
  },
  otp: {
    sentMessage: 'OTP telah dihantar ke nombor telefon bimbit anda:', 
    otpCodeLabel: 'Kod OTP:', 
    submit: 'Hantar', 
    emptyWarning: 'Sila masukkan kod OTP.', 
    verifyError: 'Tidak dapat mengesahkan OTP. Sila cuba lagi.', 
    waitBeforeSubmit:
      'Tunggu {{seconds}} saat sebelum ketik Hantar. Anda boleh masukkan kod sementara menunggu — jangan ketik Hantar berulang kali.',
    readyToSubmit: 'Anda boleh ketik Hantar sekarang.',
  },
  smokerProfile: {
    raceLabel: 'Bangsa', 
    stateLabel: 'Negeri Kediaman', 
    brandLabel: 'Jenama Rokok Semasa Anda', 
    selectRace: 'Pilih bangsa', 
    selectState: 'Pilih negeri', 
    selectBrand: 'Pilih jenama', 
    loadingBrands: 'Loading...', 
    selectBrandFirst: 'Pilih jenama dulu', 
    selectVariant: 'Pilih varian', 
    loadingVariants: 'Loading...', 
    next: 'Seterusnya', 
    brandsLoadingWarning: 'Sila tunggu sehingga senarai jenama selesai dimuatkan.', 
    variantsLoadingWarning: 'Sila tunggu sehingga senarai varian selesai dimuatkan.', 
    incompleteWarning: 'Sila jawab semua soalan sebelum meneruskan.', 
  },
  q7_1: {
    question: 'Tempat mana yang anda selalu membeli refills EVO anda?', 
    disclaimer: 'Disclaimer: Hanya tersedia di Lembah Klang, Johor dan Pulau Pinang. Terma dan syarat dikenankan', 
  },
  q7_2: {
    question: 'Berapa banyak refills EVO yang anda beli setiap kali?', 
  },
  q7_3: {
    question: 'Adakan anda mengalami "Kehabisan Stock" dalam pembelian EVO anda yang terkini?', 
  },
  q7_4: {
    question: 'Untuk membantu kami meningkatkan pengalaman pembelian anda, sila kongsikan lokasi kedai', 
    placeholder: 'Masukkan nama bangunan atau jalan', 
    searching: 'Searching...', 
    noResults: 'Tiada kedai yang sepadan. Cuba kata kunci yang berbeza.', 
    searchError: 'Tidak dapat mencari kedai. Sila cuba lagi.', 
    cantFind: 'Saya tidak dapat mencari lokasi saya', 
    submit: '', 
  },
  devReg: {
    question: 'Adakan anda telah mendaftar peranti anda?', 
    optionYes: 'Ya.', 
    optionCareContact: 'Tidak, saya ingin Consumer Care Team menghubungi saya untuk pendaftaran peranti.', 
    optionNoRegister: 'Tidak, saya tidak mahu mendaftar peranti saya.', 
  },
  tradeIn: {
    question: 'Adakah anda ingin "trade-in" peranti anda?', 
    optionYesHelp: 'Ya, saya ingin Consumer Care Team membantu saya dalam proses "trade-in" peranti.', 
    optionNotInterested: 'Tidak, saya tidak berminat untuk "trade-in" peranti saya sekarang.', 
    optionAlreadyNew: 'Tidak, saya sudah menggunakan peranti baru.', 
    tnc: 'Tertakluk kepada terma dan syarat', 
    sessionExpiredError: 'Sesi telah tamat tempoh. Sila mulakan semula dari langkah kod unik.', 
    mobileError: 'Nombor telefon bimbit tiada. Sila kembali ke langkah kod unik.', 
    incompleteError: 'Data tinjauan tidak lengkap. Sila kembali dan lengkapkan langkah-langkah sebelumnya.', 
    saveError: 'Tidak dapat menyimpan. Sila cuba lagi.', 
  },
  tradeInContact: {
    instruction: 'Sila lengkapkan maklumat anda dalam ruangan di bawah.', 
    mobileLabel: 'Telefon No:', 
    nameLabel: 'Nama:', 
    emailLabel: 'Alamat Emel:', 
    submit: 'Hantar', 
    mobileWarning: 'Sila masukkan nombor telefon', 
    nameWarning: 'Sila masukkan nama', 
    emailWarning: 'Sila masukkan alamat emel', 
    sessionExpiredWarning: 'Sesi telah tamat tempoh. Sila mulakan semula dari langkah kod unik.', 
    incompleteWarning: 'Data tinjauan tidak lengkap. Sila kembali dan lengkapkan langkah-langkah sebelumnya.', 
    pdpaNote: '* Dengan menyerahkan maklumat peribadi anda, anda setuju untuk kami guna, kumpul dan proses data peribadi anda untuk program “trade-in” tertakluk kepada syarat notis PDPA kami', 
    saveError: 'Tidak dapat menyimpan. Sila cuba lagi.', 
  },
  thankYou: {
    title: 'TERIMA KASIH', 
    subtitle: 'ATAS PENYERTAAN ANDA', 
  },
  tng: {
    title: 'TERIMA KASIH', 
    subtitle: 'ATAS PENYERTAAN ANDA', 
    smsNote: 'Anda akan menerima PIN penebusan eWallet melalui SMS', 
  },
};

// ─── Resolver ────────────────────────────────────────────────────────────────

/**
 * Returns translations for the given language.
 * Any BM string that is empty falls back to the English equivalent
 * so the app remains fully functional while BM copy is being filled in.
 */
function fallback<T extends Record<string, string>>(bmSection: T, enSection: T): T {
  return Object.fromEntries(
    Object.entries(bmSection).map(([k, v]) => [k, v || (enSection as Record<string, string>)[k]])
  ) as T;
}

export function getTranslations(lang: Lang): AppTranslations {
  if (lang === 'en') return en;
  return {
    home:            fallback(bm.home, en.home),
    ageSmoker:       fallback(bm.ageSmoker, en.ageSmoker),
    pdpa:            fallback(bm.pdpa, en.pdpa),
    uniqueCode:      fallback(bm.uniqueCode, en.uniqueCode),
    otp:             fallback(bm.otp, en.otp),
    smokerProfile:   fallback(bm.smokerProfile, en.smokerProfile),
    q7_1:            fallback(bm.q7_1, en.q7_1),
    q7_2:            fallback(bm.q7_2, en.q7_2),
    q7_3:            fallback(bm.q7_3, en.q7_3),
    q7_4:            fallback(bm.q7_4, en.q7_4),
    devReg:          fallback(bm.devReg, en.devReg),
    tradeIn:         fallback(bm.tradeIn, en.tradeIn),
    tradeInContact:  fallback(bm.tradeInContact, en.tradeInContact),
    thankYou:        fallback(bm.thankYou, en.thankYou),
    tng:             fallback(bm.tng, en.tng),
  };
}
